import { useState, type ReactNode } from 'react'
import { CardTags } from './CardTags'
import type { FeedTag } from '../../types/feedContract'
import { SharedEntryBadge } from './PostEngagement'
import { useI18n } from '../../i18n'

interface CardHeaderProps {
  username: string
  avatarUrl: string
  tags?: FeedTag[]
  creatorId?: string
  canFollow?: boolean
  isFollowing?: boolean
  followPending?: boolean
  onToggleFollow?: () => void
  action?: ReactNode
  /** Rendered right after the username, e.g. an official-account mark. */
  badge?: ReactNode
  isSharedEntry?: boolean
  isPromoted?: boolean
}

export function CardHeader({
  username,
  avatarUrl,
  tags,
  creatorId,
  canFollow = false,
  isFollowing = false,
  followPending = false,
  onToggleFollow,
  action,
  badge,
  isSharedEntry = false,
  isPromoted = false,
}: CardHeaderProps) {
  const [imgError, setImgError] = useState(false)
  const { t } = useI18n()

  const handleAvatarError = (_event: Event) => {
    setImgError(true)
  }

  return (
    <div className="card-header">
      <div className="card-avatar">
        {avatarUrl && !imgError ? (
          <img
            src={avatarUrl}
            alt={username}
            className="card-avatar-img"
            onError={handleAvatarError}
          />
        ) : (
          <div className="card-avatar-placeholder">
            {username.charAt(0).toUpperCase()}
          </div>
        )}
      </div>
      <div className="card-header-info">
        <div className="card-header-topline">
          <div className="card-header-identity">
            <span className="card-username">{username}</span>
            {badge}
            {tags && tags.length > 0 && (
              <CardTags tags={tags} className="card-tags-header" />
            )}
          </div>
          <div className="card-header-actions">
            {canFollow && creatorId && (
              <button
                type="button"
                className={`card-follow-button ${isFollowing ? 'is-following' : ''}`}
                disabled={followPending}
                onClick={onToggleFollow}
              >
                {isFollowing ? t('card.following') : t('card.follow')}
              </button>
            )}
            {action}
          </div>
        </div>
        {isPromoted ? (
          <span className="feed-card-promoted-badge">{t('card.promoted')}</span>
        ) : null}
        {isSharedEntry ? <SharedEntryBadge /> : null}
      </div>
    </div>
  )
}
